/* Owner: an "Edit" link next to a cart line item's option (both the cart page
   and the cart drawer — they share snippets/cart-items.liquid) that opens a
   variant picker so shoppers can change size/variant without leaving the cart.

   The product-page option popup (llm-option-popup.js) can't be reused as-is: it
   drives Shopify's rendered <variant-picker> DOM, which doesn't exist in the
   cart. So this is self-contained — it fetches /products/<handle>.js for the
   full variant list and commits the change through the Cart AJAX API. BUT to keep
   the look identical to the product-page picker (owner's ask), it builds the SAME
   markup/classes (.llm-option-popup-overlay/.llm-option-popup/__header/__heading/
   __close/__list/__row/...) and inherits llm-option-popup.css. All handlers are
   bound to the elements this creates (never global class delegation), so there's
   no cross-talk with the product popup's own instances.

   Changing a line's variant isn't a single Cart API call (change.js can't swap
   variant), so we ADD the new variant (preserving quantity + selling plan for
   Autoship) then remove the old line, and reload to re-render both carts with a
   guaranteed-correct state. Custom line-item properties are not carried over —
   this catalog doesn't use per-variant line properties; revisit if that changes.
*/
(function () {
  'use strict';

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.llm-cart-edit');
    if (trigger) {
      e.preventDefault();
      openPicker(trigger);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closePicker();
  });

  function money(cents) {
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        var fmt = (window.theme && theme.moneyFormat) || '${{amount}}';
        return Shopify.formatMoney(cents, fmt);
      }
    } catch (err) {
      /* fall through */
    }
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  async function openPicker(trigger) {
    var handle = trigger.dataset.handle;
    if (!handle || trigger.classList.contains('is-loading')) return;
    trigger.classList.add('is-loading');
    var product;
    try {
      product = await fetch('/products/' + handle + '.js', {
        headers: { Accept: 'application/json' },
      }).then(function (r) {
        if (!r.ok) throw new Error('product fetch ' + r.status);
        return r.json();
      });
    } catch (err) {
      trigger.classList.remove('is-loading');
      return;
    }
    trigger.classList.remove('is-loading');
    render(product, trigger.dataset);
  }

  function render(product, data) {
    closePicker();
    var currentId = String(data.variantId || '');

    // Reuse the product-page popup's classes so the styling is identical; the
    // extra llm-cart-edit-active class only carries a z-index bump (above the
    // cart drawer) and lets closePicker find this overlay.
    var overlay = el('div', 'llm-option-popup-overlay llm-cart-edit-active');
    var panel = el('div', 'llm-option-popup');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    var header = el('div', 'llm-option-popup__header');
    header.appendChild(el('div', 'llm-option-popup__heading', product.title || 'Choose an option'));
    var close = el('button', 'llm-option-popup__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&times;';
    close.addEventListener('click', closePicker);
    header.appendChild(close);
    panel.appendChild(header);

    var list = el('div', 'llm-option-popup__list');
    (product.variants || []).forEach(function (v) {
      var isCurrent = String(v.id) === currentId;
      var row = el('button', 'llm-option-popup__row');
      row.type = 'button';
      if (isCurrent) row.classList.add('is-selected');
      if (!v.available) row.classList.add('is-unavailable');

      var main = el('span', 'llm-option-popup__row-main');
      main.appendChild(el('span', 'llm-option-popup__row-name', v.public_title || v.title || v.name || 'Option'));
      if (v.sku) main.appendChild(el('span', 'llm-option-popup__row-sku', 'SKU: ' + v.sku));
      row.appendChild(main);

      if (v.available) {
        row.appendChild(el('span', 'llm-option-popup__row-price', money(v.price)));
      } else {
        var group = el('span', 'llm-option-popup__row-price-group');
        group.appendChild(el('span', 'llm-option-popup__row-price', money(v.price)));
        group.appendChild(el('span', 'llm-option-popup__row-oos', 'Out of stock'));
        row.appendChild(group);
      }

      if (isCurrent) {
        row.addEventListener('click', closePicker);
      } else if (v.available) {
        row.addEventListener('click', function () {
          commit(v.id, data, row, panel);
        });
      }
      list.appendChild(row);
    });
    panel.appendChild(list);

    var err = el('div', 'llm-cart-edit__error');
    err.setAttribute('role', 'alert');
    err.hidden = true;
    err.textContent = "Sorry, that couldn't be updated. Please try again.";
    panel.appendChild(err);

    overlay.appendChild(panel);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closePicker();
    });
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    // Force a reflow, then add is-open — synchronous so it never depends on a
    // deferred frame (requestAnimationFrame is throttled/paused in background
    // tabs), while the intervening reflow still lets the CSS transition run.
    void overlay.offsetWidth;
    overlay.classList.add('is-open');
    close.focus();
  }

  async function commit(newVariantId, data, row, panel) {
    if (panel.classList.contains('is-committing')) return;
    panel.classList.add('is-committing');
    row.classList.add('is-committing');

    var qty = parseInt(data.quantity, 10) || 1;
    var sellingPlan = data.sellingPlan ? Number(data.sellingPlan) : null;
    var addBody = { id: Number(newVariantId), quantity: qty };
    if (sellingPlan) addBody.selling_plan = sellingPlan;

    try {
      var addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(addBody),
      });
      if (!addRes.ok) throw new Error('add ' + addRes.status);

      // Remove the original line only AFTER the new one is safely in the cart. This is
      // the last mutation, so ask for the cart sections back in the same response and
      // repaint from them — a full page reload here threw away scroll position and made
      // a variant swap feel far heavier than it is. Same pattern as llm-autoship.js.
      var cartItems = document.querySelector('cart-items');
      var toRender =
        cartItems && typeof cartItems.getSectionsToRender === 'function'
          ? cartItems.getSectionsToRender()
          : null;

      var changeBody = { id: data.lineKey, quantity: 0 };
      if (toRender) {
        var sections = [];
        toRender.forEach(function (s) {
          if (sections.indexOf(s.section) === -1) sections.push(s.section);
        });
        changeBody.sections = sections;
        changeBody.sections_url = window.location.pathname;
      }

      var changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(changeBody),
      });
      if (!changeRes.ok) throw new Error('change ' + changeRes.status);
      var changeData = await changeRes.json();

      if (toRender && changeData && changeData.sections) {
        toRender.forEach(function (s) {
          var sectionEl = document.getElementById(s.id);
          if (!sectionEl) return;
          var el = sectionEl.querySelector(s.selector) || sectionEl;
          var html = changeData.sections[s.section];
          if (html != null) el.innerHTML = cartItems.constructor.getElementHTML(html, s.selector);
        });
        // Let cart listeners (counts, free-shipping bar, upsells) know.
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        closePicker();
      } else {
        // No <cart-items> to repaint (unexpected markup) — fall back to the old behaviour
        // rather than leaving the shopper looking at a stale cart.
        window.location.reload();
      }
    } catch (err) {
      panel.classList.remove('is-committing');
      row.classList.remove('is-committing');
      var box = panel.querySelector('.llm-cart-edit__error');
      if (box) box.hidden = false;
    }
  }

  function closePicker() {
    var overlay = document.querySelector('.llm-cart-edit-active');
    if (overlay) overlay.remove();
    document.body.style.overflow = '';
  }
})();
